import React, { useState, useEffect } from "react";
import {
  Modal,
  TextInput,
  Button,
  Group,
  Image,
  Alert,
  Badge,
  Code,
  Text,
  ThemeIcon,
  Loader,
  Space,
} from "@mantine/core";
import {
  IconBrandWhatsapp,
  IconCheck,
  IconCopy,
  IconAlertCircle,
} from "@tabler/icons-react";
import QRCode from "qrcode";
import { doc, setDoc, onSnapshot, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { isValidE164PhoneNumber } from "@captiva/shared-types";

export interface WhatsAppLinkModalProps {
  opened: boolean;
  onClose: () => void;
  companyId: string;
  userId: string;
  userName?: string;
  botPhoneNumber?: string;
}

/**
 * Normalizes a user-entered phone number into standard E.164 candidate format.
 * Strips whitespace, dashes, brackets, dots, and ensures a leading '+' sign.
 */
export function normalizePhoneNumber(raw: string): string {
  if (!raw) return "";
  let cleaned = raw.replace(/[\s\-().]/g, "").trim();
  if (cleaned.length > 0 && !cleaned.startsWith("+")) {
    cleaned = `+${cleaned}`;
  }
  return cleaned;
}

/**
 * Validates that user-entered phone number strictly begins with '+'
 * and conforms to international E.164 format (+xxxxxxxxxx...).
 */
export function isValidInputPhone(raw: string): boolean {
  if (!raw || typeof raw !== "string") return false;
  const trimmed = raw.trim();
  if (!trimmed.startsWith("+")) return false;
  const normalized = normalizePhoneNumber(trimmed);
  const digitsOnly = normalized.replace(/\D/g, "");
  if (digitsOnly.length < 7 || digitsOnly.length > 15) return false;
  return isValidE164PhoneNumber(normalized);
}

/**
 * Generates a random 6-digit verification code string (e.g. "482910").
 */
export function generateVerificationCode(): string {
  return Math.floor(100000 + Math.random() * 900000).toString();
}

/**
 * Generates the deep link to open WhatsApp with the prefilled LINK command.
 */
export function generateWhatsAppLink(code: string, botPhone?: string): string {
  const cleanDigits = (botPhone || "").replace(/\D/g, "");
  const message = `LINK ${code}`;
  if (cleanDigits) {
    return `https://wa.me/${cleanDigits}?text=${encodeURIComponent(message)}`;
  }
  return `https://wa.me/?text=${encodeURIComponent(message)}`;
}

export function WhatsAppLinkModal({
  opened,
  onClose,
  companyId,
  userId,
  userName,
  botPhoneNumber,
}: WhatsAppLinkModalProps) {
  const [phoneInput, setPhoneInput] = useState("");
  const [normalizedPhone, setNormalizedPhone] = useState("");
  const [code, setCode] = useState("");
  const [waLink, setWaLink] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [step, setStep] = useState<"input" | "pending" | "verified">("input");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Phone format is validated on submit (not while typing) and cleared again on edit.
  const [phoneError, setPhoneError] = useState("");
  const [copied, setCopied] = useState(false);

  // Reset state when modal is closed
  useEffect(() => {
    if (!opened) {
      if (step === "verified") {
        setStep("input");
        setPhoneInput("");
        setNormalizedPhone("");
        setCode("");
        setQrDataUrl("");
        setError("");
        setPhoneError("");
        setCopied(false);
      }
    }
  }, [opened, step]);

  // Real-time verification listener on whatsappUserIdentities/{companyId}_{phone}
  useEffect(() => {
    if (!opened || step !== "pending" || !companyId || !normalizedPhone) {
      return;
    }

    const identityDocRef = doc(
      db,
      "whatsappUserIdentities",
      `${companyId}_${normalizedPhone}`
    );

    const unsubscribe = onSnapshot(
      identityDocRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          if (data && data.verified === true) {
            setStep("verified");
          }
        }
      },
      (err) => {
        console.error("Error listening to WhatsApp verification status:", err);
      }
    );

    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [opened, step, companyId, normalizedPhone]);

  const hasPhoneInput = phoneInput.trim().length > 0;

  const handleGenerate = async () => {
    setError("");
    setPhoneError("");

    if (!isValidInputPhone(phoneInput)) {
      setPhoneError(
        "Please enter a valid international phone number in E.164 format (e.g. +27821234567)."
      );
      return;
    }

    const normalized = normalizePhoneNumber(phoneInput);

    if (!companyId || !userId) {
      setError("Company ID or User ID is missing. Please refresh and try again.");
      return;
    }

    setLoading(true);
    try {
      const generatedCode = generateVerificationCode();
      const botPhone =
        botPhoneNumber || process.env.REACT_APP_WHATSAPP_BOT_PHONE || "+14704943044";
      const link = generateWhatsAppLink(generatedCode, botPhone);

      // Generate QR Code data URL
      const qrUrl = await QRCode.toDataURL(link, {
        width: 220,
        margin: 2,
      });

      // Write pending identity document to Firestore
      const identityDocRef = doc(
        db,
        "whatsappUserIdentities",
        `${companyId}_${normalized}`
      );
      await setDoc(
        identityDocRef,
        {
          phone: normalized,
          uid: userId,
          companyId,
          code: generatedCode,
          verified: false,
          optedIn: false,
          createdAt: serverTimestamp(),
        },
        { merge: true }
      );

      setNormalizedPhone(normalized);
      setCode(generatedCode);
      setWaLink(link);
      setQrDataUrl(qrUrl);
      setStep("pending");
    } catch (err: any) {
      console.error("Failed to initiate WhatsApp link:", err);
      setError(
        err?.message || "Failed to generate WhatsApp verification link. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      const commandText = `LINK ${code}`;
      await navigator.clipboard.writeText(commandText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy command:", err);
    }
  };

  const handleReset = () => {
    setStep("input");
    setError("");
    setPhoneError("");
    setCode("");
    setQrDataUrl("");
    setCopied(false);
  };

  const handleClose = () => {
    if (step === "verified") {
      setStep("input");
      setPhoneInput("");
      setNormalizedPhone("");
      setCode("");
      setQrDataUrl("");
      setError("");
      setCopied(false);
    }
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group spacing="xs">
          <IconBrandWhatsapp size={22} color="#25D366" />
          <Text weight={600} size="md">
            Link WhatsApp Assistant {userName ? `(${userName})` : ""}
          </Text>
        </Group>
      }
      size={500}
      centered
    >
      {step === "input" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Text size="sm" color="dimmed">
            Link your personal WhatsApp number to interact directly with the
            Captiva AI Assistant. You will be able to query fuel data, reports,
            and company assets from WhatsApp.
          </Text>

          <TextInput
            label="WhatsApp Phone Number"
            placeholder="+27821234567"
            description="Enter in international E.164 format starting with '+' and country code (e.g. +27821234567)"
            value={phoneInput}
            onChange={(e) => {
              setPhoneInput(e.currentTarget.value);
              if (error) setError("");
              if (phoneError) setPhoneError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && hasPhoneInput && !loading) {
                handleGenerate();
              }
            }}
            error={phoneError || undefined}
            disabled={loading}
            required
            data-testid="whatsapp-phone-input"
          />

          {error && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Error"
              color="red"
              data-testid="whatsapp-link-error"
            >
              {error}
            </Alert>
          )}

          <Group position="right" mt="xs">
            <Button variant="default" color="gray" size="sm" style={{ minHeight: 36 }} onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              color="teal"
              size="sm"
              style={{ minHeight: 36 }}
              leftIcon={<IconBrandWhatsapp size={16} />}
              loading={loading}
              disabled={loading || !hasPhoneInput}
              onClick={handleGenerate}
              data-testid="whatsapp-generate-btn"
            >
              Generate Link & Code
            </Button>
          </Group>
        </div>
      )}

      {step === "pending" && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <div>
            <Text size="sm" color="dimmed">
              Send the verification code below from WhatsApp number{" "}
              <b>{normalizedPhone}</b> to complete identity linking.
            </Text>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              padding: "12px 20px",
              backgroundColor: "#f8f9fa",
              borderRadius: 8,
              border: "1px dashed #ced4da",
              width: "100%",
            }}
          >
            <Text size="xs" color="dimmed" weight={600} transform="uppercase">
              Your 6-Digit Verification Code
            </Text>
            <Code
              data-testid="whatsapp-verification-code"
              style={{
                fontSize: "28px",
                letterSpacing: "6px",
                fontWeight: 700,
                padding: "4px 16px",
                backgroundColor: "#e6fcf5",
                color: "#0ca678",
              }}
            >
              {code}
            </Code>
            <Button
              variant="subtle"
              size="sm"
              style={{ minHeight: 36 }}
              color={copied ? "teal" : "gray"}
              leftIcon={
                copied ? <IconCheck size={16} /> : <IconCopy size={16} />
              }
              onClick={handleCopy}
              data-testid="whatsapp-copy-code-btn"
            >
              {copied ? `Copied "LINK ${code}"!` : `Copy "LINK ${code}"`}
            </Button>
          </div>

          <Button
            component="a"
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            color="teal"
            size="md"
            fullWidth
            style={{ minHeight: 44 }}
            leftIcon={<IconBrandWhatsapp size={20} />}
            data-testid="whatsapp-open-link-btn"
          >
            Open in WhatsApp
          </Button>

          {qrDataUrl && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Image
                src={qrDataUrl}
                alt="WhatsApp Link QR Code"
                width={200}
                height={200}
                fit="contain"
                data-testid="whatsapp-qr-image"
              />
              <Text size="xs" color="dimmed">
                Scan with your phone camera or WhatsApp to send code
              </Text>
            </div>
          )}

          <Group spacing="xs" position="center">
            <Loader size="xs" color="teal" />
            <Badge color="teal" variant="dot" size="sm">
              Waiting for WhatsApp verification message...
            </Badge>
          </Group>

          <Space h="xs" />

          <Button
            variant="subtle"
            size="sm"
            style={{ minHeight: 36 }}
            color="gray"
            onClick={handleReset}
            data-testid="whatsapp-use-different-number-btn"
          >
            Use a different phone number
          </Button>
        </div>
      )}

      {step === "verified" && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 16,
            padding: "16px 0",
          }}
          data-testid="whatsapp-verified-container"
        >
          <ThemeIcon color="teal" size={56} radius="xl">
            <IconCheck size={32} />
          </ThemeIcon>

          <div>
            <Text weight={700} size="lg">
              WhatsApp Assistant Linked!
            </Text>
            <Text size="sm" color="dimmed" mt={4}>
              Phone number <b>{normalizedPhone}</b> is now verified and
              authorized to interact with your company's Captiva AI Assistant.
            </Text>
          </div>

          <Button
            color="teal"
            size="md"
            fullWidth
            style={{ minHeight: 44 }}
            onClick={handleClose}
            data-testid="whatsapp-verified-done-btn"
          >
            Done
          </Button>
        </div>
      )}
    </Modal>
  );
}

export default WhatsAppLinkModal;
